import React, { useEffect, useState, useRef, useContext } from 'react';
import { Carousel, Typography, Spin, Rate } from 'antd';
import ProductCardSkeleton from "./skeletons/ProductCardSkeleton";
import ProductServ from '../service/ProductServ';
import ProductCard from './ProductCard';
import '../css/mixedProductCarousel.css';
import SafeImage from './SafeImage';

import { LocationContext } from "../LocationContext";
import { useDeliveryEstimateContext } from "../context/DeliveryEstimateContext";
import { capitalizeFirst, renderSafeText } from '../util/unitFormatter';
import { usePreference } from '../context/PreferenceContext';
const { Title, Text } = Typography;
const MixedProductCarousel = ({ title }) => {
    const { t } = usePreference();
    const displayTitle = title || t('inspired_by_browsing');
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const carouselRef = useRef(null);
    const { latitude, longitude, radius } = useContext(LocationContext);
    const deliveryContext = useDeliveryEstimateContext();
    const registerShop = deliveryContext?.registerShop;
    const formatETA = deliveryContext?.formatETA;

    // Register shops from products only if location is available
    useEffect(() => {
        if (!latitude || !longitude) return;
        products.forEach(product => {
            const shopId = product.shopId?._id || product.shopId;
            if (shopId && registerShop) registerShop(shopId);
        });
    }, [products, registerShop, latitude, longitude]);

    useEffect(() => {
        fetchProducts();
    }, [latitude, longitude, radius]);

    const fetchProducts = async () => {
        try {
            setLoading(true);
            // Fetch products without specific filters to get a "mixed" selection
            const params = { limit: 20 };
            if (latitude && longitude) {
                params.lat = latitude;
                params.lng = longitude;
                params.radius = radius;
            }
            const res = await ProductServ.index(params);
            setProducts(res.data || []);
        } catch (err) {
            console.error("Error fetching mixed products:", err);
        } finally {
            setLoading(false);
        }
    };

    const carouselSettings = {
        infinite: false,
        dots: false,
        speed: 500,
        slidesToShow: 6,
        slidesToScroll: 1, // Safer to scroll 1 or 2 at a time
        initialSlide: 0,
        responsive: [
            { breakpoint: 1600, settings: { slidesToShow: 6, slidesToScroll: 1 } },
            { breakpoint: 1200, settings: { slidesToShow: 5, slidesToScroll: 1 } },
            { breakpoint: 992, settings: { slidesToShow: 4, slidesToScroll: 1 } },
            { breakpoint: 768, settings: { slidesToShow: 3, slidesToScroll: 1 } },
            { breakpoint: 576, settings: { slidesToShow: 2, slidesToScroll: 1 } }
        ]
    };

    if (loading && products.length === 0) {
        return (
            <div className="mixed-carousel-container">
                <div className="mixed-carousel-header">
                    <Title level={3} className="mixed-carousel-title" style={{ color: '#ffffff' }}>{displayTitle}</Title>
                </div>
                <div className="pcu-flex pcu-gap-15 pcu-overflow-hidden pcu-padding-x-20">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className="pcu-min-w-220 pcu-flex-1">
                            <ProductCardSkeleton />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (products.length === 0) return null;

    return (
        <div className="mixed-carousel-container">
            <div className="mixed-carousel-header">
                <Title level={3} className="mixed-carousel-title" style={{ color: '#ffffff' }}>{displayTitle}</Title>
            </div>

            <button className="custom-nav custom-prev" onClick={() => carouselRef.current.prev()}>‹</button>
            <button className="custom-nav custom-next" onClick={() => carouselRef.current.next()}>›</button>

            <Carousel ref={carouselRef} {...carouselSettings}>
                {(() => {
                    // Deduplicate products by name for extra safety (merges identical products with different IDs)
                    const uniqueProducts = Array.from(new Map(products
                        .filter(p => (p.product?._id ? p.product.isActive !== false : p.isActive !== false))
                        .map(p => [renderSafeText(p.name).toLowerCase(), p])).values());

                    return uniqueProducts.map((product) => (
                        <div key={product._id} className="pcu-carousel-item-wrapper">
                            <ProductCard product={product} formatETA={formatETA} />
                        </div>
                    ));
                })()}
            </Carousel>
        </div>
    );
};

export default MixedProductCarousel;
